import React from "react";
import { isMobile } from "react-device-detect";
import { trackError } from "@/utils/error-tracking";
import { asError } from "../hooks/as-error";
import ErrorBoundary from "./error-boundary";

interface AdProps {
  [key: string]: unknown;
}

class Ad extends React.Component<AdProps> {
    prevFrame: number = 0;
    initialized = false;
    insRef = React.createRef<HTMLModElement>();

    constructor(props: AdProps) {
        super(props);
        this.prevFrame = 0;
    }

    componentDidMount() {
        if (this.prevFrame) {
            window.cancelAnimationFrame(this.prevFrame);
        }
        if (this.initialized) return;

        this.initialized = true;
        this.prevFrame = window.requestAnimationFrame(() => {
            // A push fills EVERY unfilled `ins.adsbygoogle` on the page, so one
            // push per mounted ad plus a page that mounts more ads as you scroll
            // means later pushes find nothing to do and throw
            // "All 'ins' elements in the DOM ... already have ads in them" —
            // asynchronously, from Google's script, where our try/catch can't
            // reach it. Skip the push when this ad has already been filled.
            const el = this.insRef.current;
            if (el?.getAttribute('data-adsbygoogle-status')) return;

            try {
                (window.adsbygoogle = window.adsbygoogle || []).push({});
            } catch (err) {
                trackError(asError(err));
            }
        });
    }

    componentWillUnmount() {
        if (this.prevFrame) {
            window.cancelAnimationFrame(this.prevFrame);
        }
    }

    render() {
        return <ins ref={this.insRef} {...this.props} />;
    }
}

interface MobileOnlyAdState {
    mobile: boolean;
}

export class MobileOnlyAd extends React.Component<AdProps, MobileOnlyAdState> {
    constructor(props: AdProps) {
        super(props);
        this.state = { mobile: false };
    }
    
    componentDidMount(): void {
        if (isMobile) {
            this.setState({ mobile: true });
        }
    }
    
    render() {
        if (this.state.mobile) return <Ad {...this.props} />;
        return null;
    }
}

export const AdDesktop = ({ ...props }: AdProps) => (
    <ErrorBoundary returnNull>
        <Ad
            className="adsbygoogle"
            style={{ display: "block" }}
            data-ad-format="auto"
            data-full-width-responsive="true"
            {...props}
        />
    </ErrorBoundary>
);

export const AdMobile = ({ ...props }: AdProps) => (
    <ErrorBoundary returnNull>
        <MobileOnlyAd
            className="adsbygoogle"
            style={{ display: "block" }}
            data-ad-format="auto"
            data-full-width-responsive="true"
            {...props}
        />
    </ErrorBoundary>
);

export const AdAll = ({ ...props }: AdProps) => (
    <ErrorBoundary returnNull>
        <Ad
            className="adsbygoogle"
            style={{ display: "block" }}
            data-ad-format="auto"
            data-full-width-responsive="true"
            {...props}
        />
    </ErrorBoundary>
);

export default Ad;